import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
  gql,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import inflate from './inflate';
import history from './history';
import { toast } from 'react-toastify';

import * as config from 'src/shared/config';
import { decompressObject } from 'src/event/event/service/helpers';

// A list of queries that are not read-only, and are performing any kind of write operation
const READER_EXCLUDED_QUERIES = new Set([
  'paymentIntentSecret',
  'uploadProfileImage',
  'uploadLocationImage',
]);

const REFRESH_TOKEN_MUTATION = gql`
  mutation refreshToken($token: String!) {
    refreshToken(token: $token) {
      accessToken
      refreshToken
    }
  }
`;

const customFetch = (uri, options) => {
  const { operationName, query } = JSON.parse(options.body);

  if (!query?.[0] || !operationName) {
    return fetch(uri, options);
  }

  const queryType = query[0];
  const isReadQuery =
    queryType === 'q' && !READER_EXCLUDED_QUERIES.has(operationName);
  const endpoint = isReadQuery ? 'read/graphql' : 'graphql';

  const url = new URL(uri.replace('graphql', endpoint));
  url.searchParams.append('deduplicate', '1');
  url.searchParams.append(queryType, operationName);

  return fetch(url.toString(), options);
};

const hasErrorCode = (error, code) => {
  return (
    error?.graphQLErrors?.some((err) => err.code === code) &&
    history?.location?.pathname !== '/recover-password'
  );
};

const resetTokens = () => {
  const { pathname } = window.location;
  if (pathname.includes('portal/')) {
    localStorage.removeItem(config.REFRESH_TOKEN_KEY_COACH);
    localStorage.removeItem(config.ACCESS_TOKEN_KEY_COACH);
  } else {
    localStorage.removeItem(config.REFRESH_TOKEN_KEY);
    localStorage.removeItem(config.ACCESS_TOKEN_KEY);
  }
};

function getRefreshToken() {
  const { pathname } = window.location;
  if (pathname.includes('portal/')) {
    return localStorage.getItem(config.REFRESH_TOKEN_KEY_COACH);
  }
  return localStorage.getItem(config.REFRESH_TOKEN_KEY);
}

function isRefreshTokenValid(refreshToken) {
  return refreshToken && refreshToken.length > 0 && refreshToken !== 'null';
}

function createRefreshTokenHandler(apolloClient) {
  return (operation, forward) => {
    return new Observable((observer) => {
      const refreshToken = getRefreshToken();

      if (isRefreshTokenValid(refreshToken)) {
        apolloClient
          .mutate({
            mutation: REFRESH_TOKEN_MUTATION,
            variables: { token: refreshToken },
          })
          .then(({ data }) => {
            const {
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            } = data.refreshToken;

            const { pathname } = window.location;
            if (pathname.includes('portal/')) {
              localStorage.setItem(
                config.ACCESS_TOKEN_KEY_COACH,
                newAccessToken,
              );
              localStorage.setItem(
                config.REFRESH_TOKEN_KEY_COACH,
                newRefreshToken,
              );
            } else {
              localStorage.setItem(config.ACCESS_TOKEN_KEY, newAccessToken);
              localStorage.setItem(config.REFRESH_TOKEN_KEY, newRefreshToken);
            }

            // Retry the failed request
            const oldHeaders = operation.getContext().headers;
            operation.setContext({
              headers: {
                ...oldHeaders,
                Authorization: `Bearer ${newAccessToken}`,
              },
            });
            // Retry the operation
            forward(operation).subscribe({
              complete: observer.complete.bind(observer),
              error: observer.error.bind(observer),
              next: observer.next.bind(observer),
            });
          })
          .catch((error) => {
            // If refreshing fails, redirect to logout
            observer.error(error);
            const { pathname } = window.location;
            resetTokens();
            if (!config.isPublicRoute(pathname)) {
              if (pathname.includes('portal/')) {
                history.push(`/portal/login?redirect=${pathname}`);
              } else {
                history.push(`/login?redirect=${pathname}`);
              }
            } else {
              toast.error('Please login again');
              window.location.reload();
            }
          });
      } else {
        // If no valid refresh token, proceed with logout
        // observer.error(new Error('No valid refresh token'));

        const { pathname } = window.location;
        resetTokens();
        if (!config.isPublicRoute(pathname)) {
          if (pathname.includes('portal/')) {
            history.push(`/portal/login?redirect=${pathname}`);
          } else {
            history.push(`/login?redirect=${pathname}`);
          }
        }
      }
    });
  };
}

const bundleDownloadLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    forward(operation).subscribe({
      next: async (response) => {
        try {
          if (ENVIRONMENT === 'local') {
            return observer.next(response);
          }
          const processEffectiveBundle = async (targetObject) => {
            if (targetObject?.effectiveBundle) {
              const filePath = targetObject.effectiveBundle;
              const res = await fetch(filePath);
              if (!res.ok) {
                throw new Error(
                  `Failed to fetch JSON from ${filePath}: ${res.statusText}`,
                );
              }
              const json = await res.json();
              targetObject.effectiveBundle = decompressObject(json);
            }
          };

          if (response.data?.event) {
            await processEffectiveBundle(response.data.event);
          }

          if (response.data?.registration?.event) {
            await processEffectiveBundle(response.data.registration.event);
          }

          observer.next(response);
        } catch (error) {
          observer.error(error);
        } finally {
          observer.complete();
        }
      },
      error: (error) => {
        observer.error(error);
      },
    });
  });
});

const inflateLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    return inflate(response);
  });
});

const httpLink = new HttpLink({
  uri: `${config.URLS[ENVIRONMENT]}/graphql`,
  fetch: customFetch,
});

const errorLink = onError(({ forward, graphQLErrors, operation }) => {
  const { pathname } = window.location;
  if (
    hasErrorCode({ graphQLErrors }, 'CREDENTIALS_ERROR') &&
    operation.operationName !== 'authentication' &&
    config.shouldRedirectBack(pathname)
  ) {
    return createRefreshTokenHandler(apolloClient)(operation, forward);
  }

  if (hasErrorCode({ graphQLErrors }, 'CREDENTIALS_ERROR')) {
    if (pathname.includes('portal/')) {
      localStorage.removeItem(config.ACCESS_TOKEN_KEY_COACH);
      localStorage.removeItem(config.REFRESH_TOKEN_KEY_COACH);
    } else {
      localStorage.removeItem(config.ACCESS_TOKEN_KEY);
      localStorage.removeItem(config.REFRESH_TOKEN_KEY);
    }
    if (!config.isPublicRoute(pathname)) {
      if (pathname.includes('portal/')) {
        history.push(`/portal/login?redirect=${pathname}`);
      } else {
        history.push(`/login?redirect=${pathname}`);
      }
    }
  } else if (hasErrorCode({ graphQLErrors }, 'PERMISSIONS_ERROR')) {
    if (pathname.includes('portal/')) {
      history.push('/portal/403');
    } else {
      history.push('/403');
    }
  } else if (hasErrorCode({ graphQLErrors }, 'NOT_FOUND_ERROR')) {
    history.push('/404');
  } else if (hasErrorCode({ graphQLErrors }, 'SERVICE_UNAVAILABLE_ERROR')) {
    history.push('/503');
  }
});

const tokenLink = new ApolloLink((operation, forward) => {
  let currentToken = null;
  const { pathname } = window.location;
  if (pathname.includes('portal/')) {
    currentToken = localStorage.getItem(config.ACCESS_TOKEN_KEY_COACH);
  } else {
    currentToken = localStorage.getItem(config.ACCESS_TOKEN_KEY);
  }
  if (currentToken) {
    operation.setContext({
      headers: {
        Authorization: `Bearer ${currentToken}`,
      },
    });
  }

  return forward(operation);
});

const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: (error) => !!error,
  },
  delay: {
    initial: 300,
    jitter: true,
    max: Number.POSITIVE_INFINITY,
  },
});

// This is due to the Apollo v3 update, and to the fact that *Metadata types
// don't have an ID. The custom merge function won't be needed once we don't
// send metadata anymore.
const mergeMetadata = {
  fields: {
    metadata: {
      merge(existing, incoming, { mergeObjects }) {
        return mergeObjects(existing, incoming);
      },
    },
  },
};

const apolloClient = new ApolloClient({
  link: ApolloLink.from([
    inflateLink,
    bundleDownloadLink,
    tokenLink,
    errorLink,
    retryLink,
    httpLink,
  ]),
  cache: new InMemoryCache({
    typePolicies: {
      Registration: mergeMetadata,
      PaymentMethod: mergeMetadata,
      Order: mergeMetadata,
      GIS: {
        keyFields: ['zipcode'],
      },
    },
  }),
});

export default apolloClient;
